import React, { useState } from 'react';
import { Button } from './ui/button';
import { Alert, AlertDescription } from './ui/alert';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { DatabaseService } from '../lib/database-v2';
import { Shield, Check, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';

export function SuperuserSetup() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');

  const makeSuperuser = async () => {
    if (!user) {
      setStatus('error');
      setMessage('No user logged in');
      return;
    }

    setIsLoading(true);
    setStatus('idle');
    
    try {
      const db = DatabaseService.getInstance();
      
      // Check if user is already a superuser
      const isSuperAdmin = await db.isSuperAdmin(user.id);
      if (isSuperAdmin) {
        setStatus('success');
        setMessage('You are already a superuser!');
        setIsLoading(false);
        return;
      }

      // Assign super_admin role
      const success = await db.assignUserRole(user.id, 'super_admin');
      
      if (success) {
        setStatus('success');
        setMessage('Successfully made superuser! Please refresh the page to see admin features.');
      } else {
        setStatus('error');
        setMessage('Failed to assign superuser role. Check console for details.');
      }
    } catch (error) {
      console.error('Error making superuser:', error);
      setStatus('error');
      setMessage('An error occurred while assigning superuser role.');
    }
    
    setIsLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg border-0 bg-white/95 backdrop-blur">
        <CardHeader className="text-center">
          <CardTitle className="flex items-center justify-center gap-2 text-2xl">
            <Shield className="w-6 h-6 text-blue-600" />
            Bootstrap Superuser
          </CardTitle>
          <p className="text-muted-foreground text-sm mt-2">
            Initialize the first superuser account
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="text-sm text-gray-700 bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
            <p className="font-medium mb-1">Setting up admin access for:</p>
            <p className="text-blue-700 font-mono text-xs break-all">
              {user?.email || 'NO USER DETECTED'}
            </p>
          </div>

          {status === 'success' && (
            <Alert className="border-green-200 bg-green-50">
              <Check className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-700">
                {message}
                <p className="mt-2 text-sm">
                  🎉 You can now access admin features! This bootstrap button will be removed.
                </p>
              </AlertDescription>
            </Alert>
          )}

          {status === 'error' && (
            <Alert className="border-red-200 bg-red-50">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">
                {message}
              </AlertDescription>
            </Alert>
          )}

          <Button 
            onClick={makeSuperuser} 
            disabled={isLoading || status === 'success'}
            className="w-full h-12 text-lg font-medium bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Creating Superuser...
              </>
            ) : status === 'success' ? (
              <>
                <Check className="w-5 h-5 mr-2" />
                Superuser Created!
              </>
            ) : (
              <>
                <Shield className="w-5 h-5 mr-2" />
                Initialize First Superuser
              </>
            )}
          </Button>

          <div className="text-xs text-muted-foreground text-center space-y-1">
            <p>⚠️ This is a one-time bootstrap process</p>
            <p>Requires 'super_admin' role in database</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}